import { useState, useCallback } from 'react';
import type { DrillDownRow, DrillDownGranularity, ChartType, TooltipData } from '../types';
import { useChartData, useChartDimensions, useSeriesData } from '../hooks';
import {
  GridLines,
  BarSeries,
  LineSeries,
  XAxisLabels,
  ChartLegend,
  GranularityBadges,
} from './chart';
import { ChartTooltip } from '../../../shared/components';
import { formatPeriodLabel } from '../utils';

interface TimeSeriesChartProps {
  data: DrillDownRow[];
  comparisonData?: DrillDownRow[] | null;
  startDate: Date;
  endDate: Date;
  comparisonStartDate?: Date;
  comparisonEndDate?: Date;
  height?: number;
  /** Override auto-detected granularity with explicit value */
  granularity?: DrillDownGranularity;
  /** Chart type: bar or line */
  chartType?: ChartType;
  /** Selected dimensions to show unique count per period */
  selectedDimensions?: string[];
}

/**
 * Time series chart component for drill-down visualization.
 * Supports bar/line charts, comparison mode, and multi-metric display with dual-axis.
 * Includes interactive tooltips with comparison delta display.
 */
export function TimeSeriesChart({
  data,
  comparisonData,
  startDate,
  endDate,
  comparisonStartDate,
  comparisonEndDate: _comparisonEndDate, // eslint-disable-line @typescript-eslint/no-unused-vars
  height = 220,
  granularity: explicitGranularity,
  chartType = 'bar',
  selectedDimensions = [],
}: TimeSeriesChartProps): JSX.Element {
  // Tooltip state
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);

  // Stable hover handler
  const handleHover = useCallback((data: TooltipData | null) => {
    setTooltipData(data);
  }, []);

  // Process raw data into chart-ready aggregated data
  const chartData = useChartData({
    data,
    comparisonData,
    startDate,
    endDate,
    comparisonStartDate,
    explicitGranularity,
    selectedDimensions,
  });

  const {
    periods,
    currentTotals,
    comparisonTotals,
    granularity,
    hasComparison,
    metricSeries,
    hasMetricSelection,
  } = chartData;

  // Transform metric series for rendering
  const seriesData = useSeriesData({
    hasMetricSelection,
    selectedDimensions,
    metricSeries,
    periods,
    currentTotals,
    comparisonTotals,
  });

  const {
    allGroupedMetrics,
    maxValue,
    hasDualAxis,
    leftAxisGroup,
    rightAxisGroup,
  } = seriesData;

  // Calculate chart dimensions
  const dimensions = useChartDimensions({
    periods,
    height,
    hasComparison,
    hasMetricSelection,
    hasDualAxis,
    allGroupedMetrics,
    maxValue,
  });

  // Early return for empty data (AFTER all hooks)
  if (periods.length === 0) {
    return (
      <div className="p-8 text-center text-brand-gray bg-brand-dark-light rounded-monumenta">
        Sem dados para exibir
      </div>
    );
  }

  const { chartWidth } = dimensions;

  // Generate comparison period label for tooltip
  const getComparisonPeriodLabel = (): string | undefined => {
    if (!hasComparison || !comparisonStartDate || !tooltipData) return undefined;
    // Use same granularity to format comparison period
    return formatPeriodLabel(tooltipData.periodKey, granularity);
  };

  return (
    <div className="overflow-x-auto bg-brand-dark-light rounded-monumenta p-4 relative">
      <svg width={chartWidth} height={height} className="block">
        {/* Grid lines and Y-axis labels */}
        <GridLines
          dimensions={dimensions}
          maxValue={maxValue}
          hasDualAxis={hasDualAxis}
          leftAxisGroup={leftAxisGroup}
          rightAxisGroup={rightAxisGroup}
        />

        {/* Bar series - metric mode */}
        {hasMetricSelection && chartType === 'bar' && (
          <BarSeries
            mode="metric"
            periods={periods}
            dimensions={dimensions}
            allGroupedMetrics={allGroupedMetrics}
            onHover={handleHover}
          />
        )}

        {/* Line series - metric mode */}
        {hasMetricSelection && chartType === 'line' && (
          <LineSeries
            mode="metric"
            periods={periods}
            dimensions={dimensions}
            allGroupedMetrics={allGroupedMetrics}
            onHover={handleHover}
          />
        )}

        {/* Bar series - standard mode */}
        {!hasMetricSelection && chartType === 'bar' && (
          <BarSeries
            mode="standard"
            periods={periods}
            dimensions={dimensions}
            currentTotals={currentTotals}
            comparisonTotals={comparisonTotals}
            hasComparison={hasComparison}
            maxValue={maxValue}
            onHover={handleHover}
          />
        )}

        {/* Line series - standard mode */}
        {!hasMetricSelection && chartType === 'line' && (
          <LineSeries
            mode="standard"
            periods={periods}
            dimensions={dimensions}
            currentTotals={currentTotals}
            comparisonTotals={comparisonTotals}
            hasComparison={hasComparison}
            onHover={handleHover}
          />
        )}

        {/* X-axis labels */}
        <XAxisLabels
          periods={periods}
          dimensions={dimensions}
          height={height}
          hasComparison={hasComparison}
          comparisonStartDate={comparisonStartDate}
          startDate={startDate}
          granularity={granularity}
        />

        {/* Legend - standard comparison mode */}
        {hasComparison && !hasMetricSelection && (
          <ChartLegend
            mode="standard"
            dimensions={dimensions}
            height={height}
          />
        )}

        {/* Legend - metric selection mode */}
        {hasMetricSelection && (
          <ChartLegend
            mode="metric"
            dimensions={dimensions}
            allGroupedMetrics={allGroupedMetrics}
            hasDualAxis={hasDualAxis}
            leftAxisGroup={leftAxisGroup}
            rightAxisGroup={rightAxisGroup}
          />
        )}
      </svg>

      {/* Interactive tooltip */}
      {tooltipData && (
        <ChartTooltip
          visible={true}
          x={tooltipData.x}
          y={tooltipData.y}
          periodLabel={tooltipData.periodLabel}
          currentValue={tooltipData.currentValue}
          comparisonValue={tooltipData.comparisonValue}
          comparisonPeriodLabel={getComparisonPeriodLabel()}
          isCurrency={tooltipData.isCurrency ?? true}
        />
      )}

      {/* Granularity and chart type badges */}
      <GranularityBadges
        granularity={granularity}
        chartType={chartType}
        hasDualAxis={hasDualAxis}
        hasComparison={hasComparison}
        hasMetricSelection={hasMetricSelection}
        startDate={startDate}
        endDate={endDate}
      />
    </div>
  );
}
